import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { TWC_BASE_KEY, createQueryKey } from "@/api/query-keys"
import { axiosInstance } from "@/api/axios"
import {
  InventoryAllowedTypes,
  InventoryPayload,
  InventoryPayloadKey,
  InventoryPeriodDataResponse,
  InventorySetup,
  InventorySetUpResponse,
  InventoryTotals,
  PostInventoryDataPeriod,
} from "../types"
import { PeriodType, PeriodTypeQuery, PeriodTypeView } from "@/types"
import { splitPeriod } from "@/lib/utils"
import { TWC_TOTALS_KEY } from "./totals"

export const INVENTORY_KEY = createQueryKey({
  baseKey: TWC_BASE_KEY,
  keys: [`inventory`],
})

const PERIOD_DATA_KEY = createQueryKey({
  baseKey: TWC_BASE_KEY,
  keys: [`inventory`, `period-data`],
})

const TOTALS_KEY = TWC_TOTALS_KEY.concat([`inventory`])

const getInventorySetup = async (
  financialModelId: string
): Promise<InventorySetUpResponse> => {
  const response = await axiosInstance.get<InventorySetUpResponse>(
    `/financial-models/${financialModelId}/inventory/setup/`
  )
  return response.data
}

// /financial-models/{financial_model_id}/inventory/setup/

export const getInventorySetUpQueryOptions = (financialModelId: string) =>
  queryOptions({
    queryKey: INVENTORY_KEY.concat([`setup`]),
    queryFn: () => getInventorySetup(financialModelId),
  })

export const useGetInventorySetup = (financialModelId: string) =>
  useSuspenseQuery(getInventorySetUpQueryOptions(financialModelId))

const updateInventorySetup = async (
  params: Partial<InventorySetup>,
  financialModelId: string
): Promise<InventorySetUpResponse> => {
  const response = await axiosInstance.post<InventorySetUpResponse>(
    `/financial-models/${financialModelId}/inventory/setup`,
    params
  )
  return response.data
}

export const useUpdateInventorySetup = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: Partial<InventorySetup>) =>
      updateInventorySetup(params, financialModelId),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: INVENTORY_KEY,
      })
      queryClient.resetQueries({
        queryKey: PERIOD_DATA_KEY,
      })
      queryClient.resetQueries({
        queryKey: TOTALS_KEY,
      })
    },
  })
}

const getInventoryPeriodData = async (
  params: GETDeafualtParams
): Promise<InventoryPeriodDataResponse> => {
  const response = await axiosInstance.get<InventoryPeriodDataResponse>(
    `/financial-models/${params.financialModelId}/inventory/data-entry`,
    {
      params: {
        type: params.period_type,
        view: params.view,
      },
    }
  )
  return response.data
}

type GETDeafualtParams = {
  financialModelId: string
  period_type: PeriodTypeQuery
  view: PeriodTypeView
}

export const useGetInventoryQuery = (params: GETDeafualtParams) =>
  useSuspenseQuery({
    queryKey: PERIOD_DATA_KEY.concat([
      params.financialModelId,
      params.period_type,
      params.view,
    ]),
    queryFn: () => getInventoryPeriodData(params),
  })

type UpdateInventoryPeriodDataParams = {
  financialModelId: string
  data: Partial<PostInventoryDataPeriod>
}

export const updateInventoryPeriodData = async (
  params: UpdateInventoryPeriodDataParams
) => {
  const period = splitPeriod(params.data.period!)
  const { year, month } = period
  const data = {
    ...params.data,
    year: Number(year),
    month: Number(month),
  }
  const response = await axiosInstance.post<InventoryPeriodDataResponse>(
    `/financial-models/${params.financialModelId}/inventory/data-entry`,
    data
  )
  return response.data
}

export const useUpdateInventoryPeriodData = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<InventoryPayload>) =>
      updateInventoryPeriodData({ financialModelId, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERIOD_DATA_KEY.concat([financialModelId]),
      })
    },
  })
}

type SwitchInventoryColumnTypeParams = {
  financialModelId: string
  period_type: PeriodType
  input_type: InventoryAllowedTypes
  column_name: InventoryPayloadKey
  month?: number
  year?: number
  product_id?: string | null
  category_id?: string | null
}

const switchInventoryColumnType = async (
  params: SwitchInventoryColumnTypeParams
) => {
  const { financialModelId, column_name, ...rest } = params
  const response = await axiosInstance.post<InventoryPeriodDataResponse>(
    `/financial-models/${financialModelId}/inventory/config/${column_name}`,
    undefined,
    { params: rest }
  )
  return response.data
}

type SwitchInventoryQueryColumnTypeParams = Omit<
  SwitchInventoryColumnTypeParams,
  "financialModelId"
>
export const useSwitchInventoryColumnType = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: SwitchInventoryQueryColumnTypeParams) =>
      switchInventoryColumnType({
        ...params,
        financialModelId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERIOD_DATA_KEY.concat([financialModelId]),
      })
    },
  })
}

const getInventoryTotals = async (
  params: GETDeafualtParams
): Promise<InventoryTotals> => {
  const response = await axiosInstance.get<InventoryTotals>(
    `/financial-models/${params.financialModelId}/inventory/data-entry/totals`,
    {
      params: {
        type: params.period_type,
        view: params.view,
      },
    }
  )
  return response.data
}

export const useGetInventoryTotals = (params: GETDeafualtParams) =>
  useSuspenseQuery({
    queryKey: TOTALS_KEY.concat([
      params.financialModelId,
      params.period_type,
      params.view,
    ]),
    queryFn: () => getInventoryTotals(params),
  })
